import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { collection, addDoc, serverTimestamp, doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/services/firebase';
import { requestFacelockIdvSession } from '@/services/facelockSession';
import { mdlChallengeFields, MDL_CHALLENGES_COLLECTION, oneProofSessionIdFromEngage } from '@/server/mdl/annexAChallenge';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { QrCodeIcon, DocumentIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { CreditCardIcon as CreditCardSolid } from '@heroicons/react/24/solid';
import {
  engageDcApi,
  isOrgIsoMdocLikelySupported,
  requestMdocPresentation,
  submitMdocWalletResponse,
  OneProofDcApiError,
  type EngageDcApiResponse,
} from '@/services/oneproofDcApi';

interface AccountForm {
  name: string;
  email: string;
  phone: string;
  address: string;
  dateOfBirth: string;
}

type ApplicationType = 'mdl_annex_a' | 'mdl_annex_c' | 'passport' | 'form' | null;

type DcApiPhase =
  | 'idle'
  | 'engaging'
  | 'await_wallet'
  | 'verifying'
  | 'success'
  | 'error';

function applicationTypeLabel(t: ApplicationType): string {
  switch (t) {
    case 'mdl_annex_a':
      return "mDL - Annex A - REST";
    case 'mdl_annex_c':
      return "mDL - Annex C - DC API";
    case 'passport':
      return 'Passport';
    case 'form':
      return 'Manual form';
    default:
      return '—';
  }
}

function dcApiPhaseTitle(phase: DcApiPhase): string {
  switch (phase) {
    case 'engaging':
      return 'Preparing';
    case 'await_wallet':
      return 'Ready — wallet step';
    case 'verifying':
      return 'Presenting';
    case 'success':
      return 'Success';
    case 'error':
      return 'Error';
    default:
      return '';
  }
}

function hintForNoSupportedDocumentRequests(): string {
  if (typeof navigator === 'undefined') return '';
  const ua = navigator.userAgent || '';
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isChrome = /\bChrome\//.test(ua) && !/\bEdg\//.test(ua) && !/OPR\//.test(ua);

  if (isChrome && !isAndroid) {
    return '\n\nYou look to be on desktop or non-Android Chrome. The org-iso-mdoc / DC API path is aimed at phones with a wallet that registers for presentation; desktop Chrome usually has nothing to show here, so this error is normal. Try Chrome on Android with Google Wallet (if your state’s mDL is supported there), or use mDL — Annex A (QR).';
  }
  if (isAndroid) {
    return '\n\nOn Android: add your mDL to Google Wallet if your issuer supports it, update Chrome and Google Play services, then try “Present mDL from wallet” again—or use Annex A (QR).';
  }
  if (isIOS) {
    return '\n\nOn iPhone/iPad: wallet and OS support varies; check OneProof / Apple guidance for Digital Credentials and mDL—or use Annex A (QR).';
  }
  return '\n\nUse a mobile device with a compatible mDL wallet, or mDL — Annex A (QR).';
}

function formatDcFlowError(error: unknown): string {
  if (error instanceof OneProofDcApiError) {
    const hint = error.bodyText
      ? ` ${error.bodyText.slice(0, 280)}${error.bodyText.length > 280 ? '…' : ''}`
      : '';
    return `${error.message}.${hint}`;
  }
  if (error instanceof Error) {
    const m = error.message;
    if (/No supported document requests/i.test(m)) {
      return `${m}\n\nThe browser found no handler that could satisfy this mDL request (often: no wallet, or wallet has no mDL for this flow).${hintForNoSupportedDocumentRequests()}`;
    }
    if (/activation|user event/i.test(m)) {
      return `${m}\n\nBrowsers require \`navigator.credentials.get()\` to run directly from your tap. Use the “Present mDL from wallet” button right after the session is prepared (do not leave the page in between).`;
    }
    return m;
  }
  return 'An unexpected error occurred.';
}

function ApplicationStepper({ current }: { current: 1 | 2 | 3 }) {
  const steps = ['Your details', 'Verification', 'Scan or confirm'] as const;
  return (
    <ol aria-label="Application progress" className="grid grid-cols-3 gap-2 sm:gap-4">
      {steps.map((label, index) => {
        const step = (index + 1) as 1 | 2 | 3;
        const complete = step < current;
        const active = step === current;
        return (
          <li key={label} className="min-w-0" aria-current={active ? 'step' : undefined}>
            <div className={`mb-2 h-1 rounded-full ${complete || active ? 'bg-primary-700' : 'bg-slate-200'}`} />
            <p className={`text-[11px] font-semibold uppercase tracking-wide sm:text-xs ${active ? 'text-primary-800' : 'text-slate-500'}`}>
              <span className="mr-1">{step}</span>
              {label}
            </p>
          </li>
        );
      })}
    </ol>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<AccountForm>({
    name: '',
    email: '',
    phone: '',
    address: '',
    dateOfBirth: '',
  });

  const [selectedApplication, setSelectedApplication] = useState<ApplicationType>(null);
  const [qrCode, setQrCode] = useState<string>('');
  const [verificationInProgress, setVerificationInProgress] = useState(false);
  const [dcApiPhase, setDcApiPhase] = useState<DcApiPhase>('idle');
  const [dcApiMessage, setDcApiMessage] = useState<string>('');
  const [pendingEngagement, setPendingEngagement] = useState<EngageDcApiResponse | null>(null);
  const [submittingApplication, setSubmittingApplication] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (field: keyof AccountForm, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const isMdlIdentityReady = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return formData.name.trim().length > 0 && emailRegex.test(formData.email.trim());
  };

  const isFormValid = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[\d\s\-\(\)]{10,}$/;

    const validName = formData.name.trim().length > 0;
    const validEmail = emailRegex.test(formData.email.trim());
    const validPhone = phoneRegex.test(formData.phone.trim());
    const validAddress = formData.address.trim().length > 0;
    const validDateOfBirth = formData.dateOfBirth.length > 0;

    const valid = validName && validEmail && validPhone && validAddress && validDateOfBirth;

    console.log('Form validation check:', {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      address: formData.address.trim(),
      dateOfBirth: formData.dateOfBirth,
      validName,
      validEmail,
      validPhone,
      validAddress,
      validDateOfBirth,
      valid
    });
    return valid;
  };

  const saveApplicantData = async (applicationType: ApplicationType | 'manual' = null, shouldNavigate: boolean = true) => {
    try {
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || 'Unknown';
      const lastName = nameParts.slice(1).join(' ') || '';

      const applicantData = {
        firstName,
        lastName,
        email: formData.email || '',
        phone: formData.phone || '',
        dateOfBirth: formData.dateOfBirth || null,
        address: formData.address || '',
        source: applicationType || 'form',
        ...(applicationType === 'form' ? { verificationEmail: 'facelock' as const } : {}),
        applied: true,
        identityVerified: false,
        approved: false,
        appliedAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
      };

      const docRef = await addDoc(collection(db, 'applicants'), applicantData);
      console.log('Applicant saved with ID:', docRef.id);

      if (shouldNavigate) {
        navigate('/applicants');
      }

      return docRef.id;
    } catch (error) {
      console.error('Error saving applicant data:', error);

      if (error instanceof Error && (error.message?.includes('Missing or insufficient permissions') ||
          error.message?.includes('permission-denied'))) {
        console.warn('Firebase permissions not configured yet. Simulating successful save for demo purposes.');

        if (shouldNavigate) {
          setTimeout(() => {
            navigate('/applicants');
          }, 1000);
        }

        return 'demo_' + Date.now();
      }

      throw error;
    }
  };

  const saveChallenge = async (sessionId: string) => {
    try {
      const now = new Date();
      const expireAfterDate = new Date(now.getTime() + (2 * 24 * 60 * 60 * 1000));

      const challengeData = {
        createdDate: serverTimestamp(),
        expireAfterDate: expireAfterDate,
        sessionId: sessionId,
        sessionType: 'applicant'
      };

      const docRef = await addDoc(collection(db, 'challenges'), challengeData);
      console.log('Challenge record created with ID:', docRef.id, 'for sessionId:', sessionId);

      return docRef.id;
    } catch (error) {
      console.error('Error saving challenge record:', error);
      throw error;
    }
  };

  const generateQRCode = async (applicationType: 'mdl_annex_a' | 'passport') => {
    if (!isMdlIdentityReady()) return;
    setVerificationInProgress(true);
    let qrCodeDataURL: string | null = null;

    try {
      if (applicationType === 'mdl_annex_a') {
        try {
          console.log('Calling mDL engagement API (Annex A REST)...');
          const response = await fetch('https://mdl.envoc.live/api/engage');
          console.log('API response status:', response.status);

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const engagementData = await response.json();
          console.log('API response data:', engagementData);

          if (engagementData && engagementData.data_b64) {
            qrCodeDataURL = await QRCode.toDataURL(engagementData.data_b64, {
              width: 256,
              margin: 2,
              color: {
                dark: '#1e40af',
                light: '#ffffff',
              },
            });

            const sessionId = oneProofSessionIdFromEngage(engagementData);
            console.log('mDL QR Code generated from API:', {
              sessionID: sessionId,
              engagementLength: engagementData.data_b64.length
            });

            if (sessionId) {
              try {
                await saveChallenge(sessionId);
                await setDoc(doc(db, MDL_CHALLENGES_COLLECTION, sessionId), mdlChallengeFields({
                  sessionId,
                  email: formData.email,
                  name: formData.name,
                }));
                console.log('Challenge record saved successfully for sessionId:', sessionId);
              } catch (challengeError) {
                console.error('Failed to save challenge record:', challengeError);
              }
            }
          } else {
            throw new Error('Invalid API response: missing readerEngagement data');
          }
        } catch (mdlError) {
          console.warn('Failed to fetch mDL engagement data, using fallback:', mdlError);
          const sessionData = {
            applicationType,
            timestamp: new Date().toISOString(),
            sessionId: `mdl_fallback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            userData: formData,
            error: 'API unavailable - using demo QR code'
          };

          const qrData = JSON.stringify(sessionData);
          qrCodeDataURL = await QRCode.toDataURL(qrData, {
            width: 256,
            margin: 2,
            color: {
              dark: '#1e40af',
              light: '#ffffff',
            },
          });
        }
      } else {
        const sessionData = {
          applicationType,
          timestamp: new Date().toISOString(),
          sessionId: `passport_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          userData: formData,
        };

        const qrData = JSON.stringify(sessionData);
        qrCodeDataURL = await QRCode.toDataURL(qrData, {
          width: 256,
          margin: 2,
          color: {
            dark: '#16a34a',
            light: '#ffffff',
          },
        });
      }

      if (qrCodeDataURL) {
        setQrCode(qrCodeDataURL);
        console.log('QR code set successfully');
      } else {
        throw new Error('QR code data URL is null');
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
      try {
        const fallbackData = `fallback_${applicationType}_${Date.now()}`;
        qrCodeDataURL = await QRCode.toDataURL(fallbackData, {
          width: 256,
          margin: 2,
        });
        setQrCode(qrCodeDataURL);
        console.log('Fallback QR code generated and set');
      } catch (fallbackError) {
        console.error('Fallback QR code generation also failed:', fallbackError);
      }
    } finally {
      setVerificationInProgress(false);
    }
  };

  const handleAnnexARest = async () => {
    if (!isMdlIdentityReady()) return;
    setSelectedApplication('mdl_annex_a');
    setDcApiPhase('idle');
    setDcApiMessage('');
    setPendingEngagement(null);
    setQrCode('');

    try {
      console.log('Skipping applicant data save for mDL Annex A — handled by separate process');
      await generateQRCode('mdl_annex_a');
    } catch (error) {
      console.error('Error processing Annex A:', error);
      setSelectedApplication(null);
    }
  };

  const handleAnnexCDcApi = async () => {
    if (!isMdlIdentityReady()) return;
    setSelectedApplication('mdl_annex_c');
    setQrCode('');
    setPendingEngagement(null);
    setDcApiPhase('engaging');
    setDcApiMessage('Contacting OneProof…');
    setVerificationInProgress(true);

    try {
      const supported = await isOrgIsoMdocLikelySupported();
      if (!supported) {
        setDcApiPhase('error');
        setDcApiMessage(
          'This device or browser does not report support for org-iso-mdoc. Use a compatible wallet and browser (see OneProof DC API integration guide), or use mDL - Annex A - REST.'
        );
        return;
      }

      const engagement = await engageDcApi();
      try {
        await saveChallenge(engagement.sessionID);
        await setDoc(doc(db, MDL_CHALLENGES_COLLECTION, engagement.sessionID), mdlChallengeFields({
          sessionId: engagement.sessionID,
          email: formData.email,
          name: formData.name,
        }));
      } catch (challengeError) {
        console.error('Failed to save challenge record:', challengeError);
      }

      setPendingEngagement(engagement);
      setDcApiPhase('await_wallet');
      setDcApiMessage(
        'Session is ready. Tap “Present mDL from wallet” below. Chrome and other browsers require this as a separate tap so the wallet prompt is tied to your gesture (not to the network request that prepared the session).'
      );
    } catch (error) {
      setDcApiPhase('error');
      setDcApiMessage(formatDcFlowError(error));
      console.error('Annex C DC API engage failed:', error);
    } finally {
      setVerificationInProgress(false);
    }
  };

  /**
   * Must stay synchronous up to `requestMdocPresentation()` so `credentials.get()` runs under user activation.
   */
  const handlePresentWalletTap = () => {
    const eng = pendingEngagement;
    if (!eng) return;

    setVerificationInProgress(true);
    setDcApiPhase('verifying');
    setDcApiMessage('Opening your wallet…');

    const credPromise = requestMdocPresentation(eng);
    void credPromise
      .then((credential) => {
        if (!credential) {
          throw new Error('No credential was returned (you may have cancelled).');
        }
        setDcApiMessage('Sending response to OneProof…');
        return submitMdocWalletResponse(eng.sessionID, credential);
      })
      .then(() => {
        setPendingEngagement(null);
        setDcApiPhase('success');
        setDcApiMessage(
          'Verification completed. Your credential was checked with OneProof (details are not stored in this demo).'
        );
      })
      .catch((error: unknown) => {
        setDcApiPhase('error');
        setDcApiMessage(formatDcFlowError(error));
        setPendingEngagement(null);
        console.error('Annex C wallet presentation failed:', error);
      })
      .finally(() => {
        setVerificationInProgress(false);
      });
  };

  const handlePassport = async () => {
    if (!isMdlIdentityReady()) return;
    setSelectedApplication('passport');
    setDcApiPhase('idle');
    setDcApiMessage('');
    setPendingEngagement(null);
    setQrCode('');

    try {
      await saveApplicantData('passport', true);
      await generateQRCode('passport');
    } catch (error) {
      console.error('Error processing passport:', error);
      setSelectedApplication(null);
    }
  };

  const handleManualSubmit = async () => {
    if (!isFormValid() || submittingApplication) return;

    setSubmittingApplication(true);
    setSubmitError('');
    try {
      const applicantId = await saveApplicantData('form', false);
      const realApplicantId = applicantId && !applicantId.startsWith('demo_') ? applicantId : undefined;
      try {
        const session = await requestFacelockIdvSession({
          applicantId: realApplicantId,
          email: formData.email.trim(),
          name: formData.name.trim(),
          phone: formData.phone.trim(),
          dateOfBirth: formData.dateOfBirth,
          address: formData.address.trim(),
        });
        if (realApplicantId && !session.persisted) {
          try {
            await updateDoc(doc(db, 'applicants', realApplicantId), {
              facelockSessionId: session.sessionId,
              facelockSessionUrl: session.url,
              facelockStatus: 'pending',
              facelockEmailSent: session.emailed,
              lastUpdated: new Date().toISOString(),
            });
          } catch (persistError) {
            console.error('Failed to store FaceLock session id on the applicant:', persistError);
          }
        }
        const notice = session.emailed
          ? 'A FaceLock identity verification link was emailed to the address on the application.'
          : 'The application was saved and a FaceLock session was created, but the verification email could not be sent.';
        navigate('/applicants', { state: { facelockNotice: notice } });
      } catch (sessionError) {
        console.error('FaceLock IDV session was not created:', sessionError);
        navigate('/applicants', {
          state: {
            facelockNotice: 'The application was saved, but the FaceLock verification email could not be sent.',
          },
        });
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setSubmitError('Could not save the application. Please try again.');
    } finally {
      setSubmittingApplication(false);
    }
  };

  const identityReady = isMdlIdentityReady();
  const reachedScan = (Boolean(qrCode) && identityReady) || dcApiPhase === 'success';
  const currentStep: 1 | 2 | 3 = reachedScan ? 3 : identityReady ? 2 : 1;

  return (
    <div className="mx-auto max-w-5xl motion-safe:animate-fade-in">
      <div className="mb-6 sm:mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary-700">Business First Bank</p>
        <h2 className="mt-2 font-serif text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">Open Your Account</h2>
        <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
          Join Business First Bank with our secure biometric verification process. Submit the application, or verify with a mobile driver&apos;s license or passport.
        </p>
      </div>

      <div className="mb-6 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm sm:px-6">
        <ApplicationStepper current={currentStep} />
      </div>

      <div className="space-y-6">
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-8" aria-labelledby="account-information-heading">
            <h3 id="account-information-heading" className="mb-6 font-serif text-2xl font-semibold text-slate-950">Account Information</h3>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Input
                label="Full Name"
                type="text"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />

              <Input
                label="Email Address"
                type="email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />

              <Input
                label="Phone Number"
                type="tel"
                placeholder="Enter your phone number"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />

              <Input
                label="Date of Birth"
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
              />
            </div>

            <div className="mt-6">
              <Input
                label="Address"
                type="text"
                placeholder="Enter your complete address"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
              />
            </div>

            <div className="mt-8 border-t border-slate-200 pt-6">
              <div className="flex justify-stretch sm:justify-center">
                <Button
                  onClick={() => handleManualSubmit()}
                  disabled={!isFormValid() || submittingApplication}
                  size="lg"
                  className="w-full sm:w-auto sm:px-8"
                >
                  {submittingApplication ? 'Sending verification email…' : 'Submit Application'}
                </Button>
              </div>
              {submitError && (
                <p className="mt-3 text-center text-sm text-red-800" role="alert">{submitError}</p>
              )}
              <p className="mt-3 text-center text-sm text-slate-600">
                Or choose one of the biometric verification options below
              </p>
            </div>
          </section>

          <section className="space-y-5" aria-labelledby="verification-method-heading">
            <div>
              <h3 id="verification-method-heading" className="font-serif text-2xl font-semibold text-slate-950">
                Choose Your Verification Method (Optional)
              </h3>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
                For enhanced security, choose a biometric verification method below, or submit manually above
              </p>
            </div>

            {!isMdlIdentityReady() && (
              <p className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-950" role="status">
                Enter a valid name and email before generating an mDL or passport QR code.
              </p>
            )}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                <div className="mb-4 flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-800">
                    <CreditCardSolid className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <h4 className="text-lg font-semibold text-slate-950">Mobile Driver&apos;s License</h4>
                    <p className="text-sm text-slate-600">Annex A (QR + REST) or Annex C (DC API wallet)</p>
                  </div>
                </div>
                <p className="text-sm leading-6 text-slate-600">
                    <strong className="font-semibold text-slate-800">Annex A</strong> shows a QR code to scan with your wallet (Envoc REST engage).
                    <strong className="font-semibold text-slate-800"> Annex C</strong> uses the Digital Credentials API with OneProof (two steps: prepare, then tap &quot;Present mDL from wallet&quot; so the browser allows the wallet prompt). Expect this to work on a <strong className="font-semibold text-slate-800">phone with a compatible wallet</strong> (e.g. Chrome on Android + Google Wallet where supported); desktop Chrome often has no mDL presenter.
                    Production may require Chrome origin trials and correct CORS on the OneProof host (see deployment docs).
                </p>

                  <div className="mt-5 space-y-3">
                    <Button
                      onClick={() => void handleAnnexARest()}
                      disabled={!isMdlIdentityReady() || verificationInProgress}
                      className="w-full"
                      size="lg"
                    >
                      {verificationInProgress && selectedApplication === 'mdl_annex_a' ? (
                        'Generating QR Code…'
                      ) : (
                        <>
                          <QrCodeIcon className="mr-2 w-5 h-5" />
                          mDL - Annex A - REST
                        </>
                      )}
                    </Button>

                    <Button
                      onClick={() => void handleAnnexCDcApi()}
                      disabled={!isMdlIdentityReady() || verificationInProgress}
                      variant="outline"
                      className="w-full"
                      size="lg"
                    >
                      {verificationInProgress && selectedApplication === 'mdl_annex_c' ? (
                        'Working…'
                      ) : (
                        <>
                          <DevicePhoneMobileIcon className="mr-2 w-5 h-5" />
                          mDL - Annex C - DC API
                        </>
                      )}
                    </Button>
                  </div>
              </div>

              <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                <div className="mb-4 flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-800">
                    <DocumentIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <h4 className="text-lg font-semibold text-slate-950">Passport Application</h4>
                    <p className="text-sm text-slate-600">Verify with your passport</p>
                  </div>
                </div>
                <p className="mb-6 flex-1 text-sm leading-6 text-slate-600">
                    Use your passport for secure identity verification. Generate a QR code to
                    start the biometric verification process.
                </p>

                  <Button
                    onClick={() => void handlePassport()}
                    disabled={!isMdlIdentityReady() || verificationInProgress}
                    className="w-full"
                    size="lg"
                  >
                    {verificationInProgress && selectedApplication === 'passport' ? (
                      'Generating QR Code…'
                    ) : (
                      <>
                        <QrCodeIcon className="mr-2 w-5 h-5" />
                        Generate Passport QR Code
                      </>
                    )}
                  </Button>
              </div>
            </div>

            {selectedApplication === 'mdl_annex_c' && dcApiPhase !== 'idle' && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-8" role="status">
                <h4 className="mb-3 font-serif text-xl font-semibold text-slate-950">
                  mDL — Annex C (DC API)
                </h4>
                <div
                  className={`rounded-xl border p-4 text-sm leading-6 ${
                    dcApiPhase === 'success'
                      ? 'border-green-300 bg-green-50 text-green-950'
                      : dcApiPhase === 'error'
                        ? 'border-red-300 bg-red-50 text-red-950'
                        : 'border-primary-200 bg-primary-50 text-primary-950'
                  }`}
                >
                  <p className="font-semibold">{dcApiPhaseTitle(dcApiPhase)}</p>
                  <p className="mt-2 whitespace-pre-wrap">{dcApiMessage}</p>
                  {dcApiPhase === 'await_wallet' && pendingEngagement && (
                    <div className="mt-4">
                      <Button
                        type="button"
                        onClick={handlePresentWalletTap}
                        disabled={verificationInProgress}
                        className="w-full sm:w-auto"
                        size="lg"
                      >
                        {verificationInProgress ? 'Opening wallet…' : 'Present mDL from wallet'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {qrCode && isMdlIdentityReady() && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-sm sm:p-8">
                <h4 className="mb-2 font-serif text-2xl font-semibold text-slate-950">
                  Scan QR Code to Complete Verification
                </h4>
                <p className="mx-auto mb-5 max-w-md text-sm leading-6 text-slate-600">
                  Use your mobile device to scan this QR code and complete your account opening process
                </p>
                <div className="flex justify-center">
                  <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
                    <img
                      src={qrCode}
                      alt="Verification QR Code"
                      className="mx-auto h-48 w-48 sm:h-64 sm:w-64"
                    />
                  </div>
                </div>
                <p className="mx-auto mt-5 inline-flex max-w-full rounded-full border border-primary-200 bg-primary-50 px-4 py-2 text-sm text-primary-900">
                  <strong className="font-semibold">Application Type:</strong>&nbsp;{applicationTypeLabel(selectedApplication)}
                </p>
              </div>
            )}
          </section>
      </div>
    </div>
  );
}
